import { clientWithApiClient } from "../../client";
import type { ApiClient } from "../../types";
import type { Response } from "../../types";
import type { CreateBookingRequest, BookingResponse } from "./types";
import type { WithBookingWarnings } from "../booking-requests/types";

/**
 * Creates a new booking with payment processing
 *
 * This endpoint handles the complete booking flow including:
 * - Customer creation/validation
 * - Quote creation
 * - Rental creation
 * - Payment processing with Stripe
 * - 3D Secure authentication if required
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the booking
 * @param bookingData - Complete booking data including customer, vehicle, dates, and payment
 * @returns Promise that resolves to the booking response with rental and payment details
 *
 * @throws {Error} If the booking data is invalid or payment fails
 *
 * @example
 * ```typescript
 * const { data: booking } = await bookings.create(apiClient, "fleet-123", {
 *   booking: {
 *     customer_name: "John Doe",
 *     customer_email: "john@example.com",
 *     customer_phone: "+1234567890",
 *     pick_up: "2025-09-23",
 *     return: "2025-09-26",
 *     vehicle_id: "vehicle-123",
 *     notes: "Special requirements",
 *     theme: "light",
 *     guests: {
 *       adults: 2,
 *       children: 1,
 *       infants: 0,
 *       pets: 0
 *     },
 *     payment: {
 *       stripe_payment_method_id: "pm_1234567890",
 *       card: {
 *         last4: "4242",
 *         brand: "visa",
 *         exp_year: 2026,
 *         exp_month: 2
 *       },
 *       currency: "USD",
 *       description: "RV Rental Payment - embed checkout"
 *     },
 *     daily_rates: [
 *       {
 *         date: "2025-09-23",
 *         quoted_amount: "120.00",
 *         rate_plan_id: "rate-plan-123",
 *         notes: "Day 2025-09-23"
 *       }
 *     ]
 *   }
 * });
 * ```
 */
const create = (
  apiClient: ApiClient,
  fleetId: string,
  bookingData: CreateBookingRequest,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/bookings?${queryString}` : "/bookings";

  return clientWithApiClient<WithBookingWarnings<Response<BookingResponse>>>(
    apiClient,
    url,
    {
      method: "POST",
      body: JSON.stringify(bookingData),
    },
  );
};

/**
 * Retrieves a specific booking by ID
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the booking
 * @param bookingId - The ID of the booking to retrieve
 * @returns Promise that resolves to the booking response with rental and payment details
 *
 * @throws {Error} If the booking is not found or access is denied
 *
 * @example
 * ```typescript
 * const { data: booking } = await bookings.get(apiClient, "fleet-123", "booking-456");
 * console.log(booking.rental.status); // "confirmed"
 * console.log(booking.payment.status); // "succeeded"
 * ```
 */
const get = (apiClient: ApiClient, fleetId: string, bookingId: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = `/bookings/${bookingId}?${queryString}`;

  return clientWithApiClient<Response<BookingResponse>>(apiClient, url, {
    method: "GET",
  });
};

/**
 * Retries payment for an existing booking
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the booking
 * @param bookingId - The ID of the booking to retry payment for
 * @param paymentData - New payment method data
 * @returns Promise that resolves to the booking response with updated payment details
 *
 * @throws {Error} If the payment retry fails
 *
 * @example
 * ```typescript
 * const { data: booking } = await bookings.retryPayment(apiClient, "fleet-123", "booking-456", {
 *   stripe_payment_method_id: "pm_1234567890",
 *   card: {
 *     brand: "visa",
 *     last4: "4242",
 *     exp_month: 12,
 *     exp_year: 2025
 *   }
 * });
 * ```
 */
const retryPayment = (
  apiClient: ApiClient,
  fleetId: string,
  bookingId: string,
  paymentData: {
    stripe_payment_method_id: string;
    card: {
      brand: string;
      last4: string;
      exp_month: number;
      exp_year: number;
    };
  },
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = `/bookings/${bookingId}/retry-payment?${queryString}`;

  return clientWithApiClient<Response<BookingResponse>>(apiClient, url, {
    method: "POST",
    body: JSON.stringify({ retry_payment: paymentData }),
  });
};

export const bookings = {
  create,
  get,
  retryPayment,
};
